'use client';
import {
  Title,
  Button,
  Text,
  InputForm,
  InputPassword,
  OauthButton,
  Alert,
  AlertDescription,
  AlertTitle,
} from '@ui/components';
import { useLoginState, LoginForm } from './login.state';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';

interface ILogin {
  title: string;
  onLogin(form: LoginForm, errorCb: (e: string) => void): void;
  oAuth?: boolean;
  oAuthProps?: React.ComponentProps<typeof OauthButton>;
  isInAppBrowser?: boolean;
}

export function LoginV2Component({
  title,
  onLogin,
  oAuth = false,
  isInAppBrowser = false,
}: ILogin) {
  const { email, password, isLoading, onSubmit } = useLoginState(onLogin);

  const searchParams = useSearchParams();
  const badgeId = searchParams.get('badgeId');
  const userId = searchParams.get('userId');
  const claimCode = searchParams.get('claimCode');
  const suggestion = searchParams.get('suggestion');
  const productId = searchParams.get('productId');
  const urlRedirect = searchParams.get('url-redirect');
  const creatorId = searchParams.get('creatorId');

  const params = new URLSearchParams();

  if (badgeId) {
    params.set('badgeId', badgeId);
  }

  if (claimCode) {
    params.set('claimCode', claimCode);
  }

  if (userId) {
    params.set('userId', userId);
  }

  if (productId) {
    params.set('productId', productId);
  }

  if (creatorId) {
    params.set('creatorId', creatorId);
  }

  if (urlRedirect) {
    params.set('url-redirect', urlRedirect);
  }

  const signupHref = `/auth/signup?${params.toString()}`;

  return (
    <div className="flex h-full w-full flex-col justify-center items-center space-y-4">
      {suggestion === 'sign-in' && (
        <Alert variant="destructive" className="rounded">
          <AlertTitle>
            You have already signed up with this email. Please sign in to continue.
          </AlertTitle>
          <AlertDescription>If you have forgotten your password, please reset it.</AlertDescription>
        </Alert>
      )}

      <Title type="l" className="text-center lg:text-left">
        {title}
      </Title>

      {isInAppBrowser && (
        <>
          {oAuth && (
            <OauthButton
              type="google"
              url="/auth/google"
              badgeId={badgeId}
              claimCode={claimCode}
              claim="true"
            />
          )}
          {oAuth && (
            <div className="my-6 flex w-full items-center">
              <div className="flex-1 border-t border-gray-200" />
              <span className="mx-4 text-gray-400">or</span>
              <div className="flex-1 border-t border-gray-200" />
            </div>
          )}
        </>
      )}

      <div className="mb-10 w-full gap-2 space-y-2">
        <InputForm label="Email" form={email} disabled={isLoading} />
        <InputPassword
          label="Password"
          form={password}
          disabled={isLoading}
          labelComponent={
            <Link href="/auth/forgot-password" className="h-auto w-fit cursor-pointer px-3">
              <Text type="s" className="text-[#1064D6] underline">
                Forgot your password?
              </Text>
            </Link>
          }
        />
      </div>

      <Button onClick={onSubmit} type="primary" isLoading={isLoading} className="w-full">
        Login
      </Button>

      <Text type="m" className="mt-4 text-center">
        <span>Don&apos;t have an account? </span>
        <Link href={signupHref} className="cursor-pointer text-[#FF6600] font-semibold">
          Sign up
        </Link>
      </Text>
    </div>
  );
}
